// src/pages/PrivacyPolicy.tsx
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";
import { useTheme } from "../context/ThemeContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import PublicHeader from "../components/PublicHeader";
import { C, FONT } from "../theme";

// ── Responsive hook ────────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const RankingsIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const LockIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const MailIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const HomeIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

// ── RichText: renders simple Markdown-like body text ──────────────────────────
// Handles: **bold**, _italic_, - bullet lists, \n newlines
function RichText({ text, style }: { text: string; style?: React.CSSProperties }) {
  if (!text) return null;

  // Split into lines first
  const lines = text.split(/\n/);

  const renderInline = (line: string, key: number): React.ReactNode => {
    // Split on **bold** and _italic_ markers
    const parts: React.ReactNode[] = [];
    const regex = /\*\*(.+?)\*\*|_(.+?)_/g;
    let last = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(line)) !== null) {
      if (match.index > last) parts.push(line.slice(last, match.index));
      if (match[1] !== undefined) {
        parts.push(<strong key={match.index} style={{ color: C.text, fontWeight: 700 }}>{match[1]}</strong>);
      } else if (match[2] !== undefined) {
        parts.push(<em key={match.index} style={{ fontStyle: "italic", opacity: 0.8 }}>{match[2]}</em>);
      }
      last = match.index + match[0].length;
    }
    if (last < line.length) parts.push(line.slice(last));
    return <React.Fragment key={key}>{parts}</React.Fragment>;
  };

  const nodes: React.ReactNode[] = [];
  let bulletGroup: string[] = [];

  const flushBullets = () => {
    if (bulletGroup.length > 0) {
      nodes.push(
        <ul key={`ul-${nodes.length}`} style={{ margin: "6px 0 6px 0", paddingLeft: 18, listStyle: "disc" }}>
          {bulletGroup.map((b, i) => (
            <li key={i} style={{ marginBottom: 3 }}>{renderInline(b, i)}</li>
          ))}
        </ul>
      );
      bulletGroup = [];
    }
  };

  lines.forEach((line, i) => {
    const trimmed = line.trim();
    if (trimmed.startsWith("- ") || trimmed.startsWith("• ")) {
      bulletGroup.push(trimmed.replace(/^[-•]\s+/, ""));
    } else {
      flushBullets();
      if (trimmed === "") {
        nodes.push(<br key={`br-${i}`} />);
      } else {
        nodes.push(<span key={i} style={{ display: "block", marginBottom: 2 }}>{renderInline(trimmed, i)}</span>);
      }
    }
  });
  flushBullets();

  return <div style={{ margin: 0, ...style }}>{nodes}</div>;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function PrivacyPolicy() {
  const { t } = useTranslation();
  const { resolvedTheme } = useTheme();
  const darkTheme = resolvedTheme === "dark";
  const navigate = useNavigate();
  const w = useWindowWidth();
  const isMobile = w < 768;
  const [heroLoaded, setHeroLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 80);
    return () => clearTimeout(timer);
  }, []);

  const sections: Array<{ titleKey: string; bodyKey: string }> = [
    { titleKey: "privacyPolicy.sections.definitions.title",            bodyKey: "privacyPolicy.sections.definitions.body" },
    { titleKey: "privacyPolicy.sections.whoWeAre.title",               bodyKey: "privacyPolicy.sections.whoWeAre.body" },
    { titleKey: "privacyPolicy.sections.dataWeCollect.title",          bodyKey: "privacyPolicy.sections.dataWeCollect.body" },
    { titleKey: "privacyPolicy.sections.howWeCollect.title",           bodyKey: "privacyPolicy.sections.howWeCollect.body" },
    { titleKey: "privacyPolicy.sections.purposeLegalBasis.title",      bodyKey: "privacyPolicy.sections.purposeLegalBasis.body" },
    { titleKey: "privacyPolicy.sections.howWeUse.title",               bodyKey: "privacyPolicy.sections.howWeUse.body" },
    { titleKey: "privacyPolicy.sections.dataSharing.title",            bodyKey: "privacyPolicy.sections.dataSharing.body" },
    { titleKey: "privacyPolicy.sections.internationalTransfers.title",  bodyKey: "privacyPolicy.sections.internationalTransfers.body" },
    { titleKey: "privacyPolicy.sections.retention.title",              bodyKey: "privacyPolicy.sections.retention.body" },
    { titleKey: "privacyPolicy.sections.security.title",               bodyKey: "privacyPolicy.sections.security.body" },
    { titleKey: "privacyPolicy.sections.children.title",               bodyKey: "privacyPolicy.sections.children.body" },
    { titleKey: "privacyPolicy.sections.payment.title",                bodyKey: "privacyPolicy.sections.payment.body" },
    { titleKey: "privacyPolicy.sections.cookies.title",                bodyKey: "privacyPolicy.sections.cookies.body" },
    { titleKey: "privacyPolicy.sections.yourRights.title",             bodyKey: "privacyPolicy.sections.yourRights.body" },
    { titleKey: "privacyPolicy.sections.changes.title",                bodyKey: "privacyPolicy.sections.changes.body" },
    { titleKey: "privacyPolicy.sections.contactInfo.title",            bodyKey: "privacyPolicy.sections.contactInfo.body" },
    { titleKey: "privacyPolicy.sections.governingLaw.title",           bodyKey: "privacyPolicy.sections.governingLaw.body" },
    { titleKey: "privacyPolicy.sections.lastUpdateInfo.title",         bodyKey: "privacyPolicy.sections.lastUpdateInfo.body" },
  ];

  return (
    <>
    <Helmet>
      <title>{t("seo.privacy.title")} - {t("privacyPolicy.subtitle")}</title>
      <meta name="description" content={t("seo.privacy.description")} />
      <link rel="canonical" href="https://registration.taekwondo.ge/privacy" />
      <meta property="og:title" content={`${t("seo.privacy.title")} - ${t("privacyPolicy.subtitle")}`} />
      <meta property="og:description" content={t("seo.privacy.description")} />
      <meta property="og:url" content="https://registration.taekwondo.ge/privacy" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://registration.taekwondo.ge/geo.png" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={`${t("seo.privacy.title")} - ${t("privacyPolicy.subtitle")}`} />
      <meta name="twitter:description" content={t("seo.privacy.description")} />
      <meta name="twitter:image" content="https://registration.taekwondo.ge/geo.png" />
      <link rel="alternate" hrefLang="en" href="https://registration.taekwondo.ge/privacy" />
      <link rel="alternate" hrefLang="ka" href="https://registration.taekwondo.ge/privacy" />
      <link rel="alternate" hrefLang="x-default" href="https://registration.taekwondo.ge/privacy" />
    </Helmet>
      <style>{`
        @keyframes heroBgZoom {
          from { transform: scale(1.07); }
          to   { transform: scale(1); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        .pp-loaded .pp-anim-0 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.08s both; }
        .pp-loaded .pp-anim-1 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.2s both; }
        .pp-loaded .pp-anim-2 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.32s both; }
        .pp-loaded .pp-anim-3 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.44s both; }
        .pp-loaded .pp-anim-nav { animation: fadeIn 0.5s ease 0.05s both; }

        .pp-back-btn:hover  { color: var(--color-text) !important; border-color: var(--color-text-subtle) !important; }
        .pp-nav-link:hover  { color: var(--color-accent) !important; }
        .pp-section-card:hover { border-color: rgba(218,41,28,0.22) !important; transform: translateY(-2px); box-shadow: var(--shadow-raised) !important; }
        .pp-email-btn:hover { opacity: 0.85 !important; transform: translateY(-1px) !important; }
        .pp-home-btn:hover  { background: var(--color-border) !important; border-color: var(--color-border-strong) !important; color: var(--color-text) !important; }

        * { box-sizing: border-box; }
      `}</style>

      <div style={s.page}>

        {/* ══════════════ NAVBAR ══════════════════════════════════════════════ */}
        <PublicHeader backTo="/" backLabel={t("nav.backToHome")} />
        {false && <header className="pp-anim-nav" style={{
          ...s.navbar,
          padding: isMobile ? "0 14px" : "0 32px",
        }}>
          {/* Left: Back button */}
          <button className="pp-back-btn" style={s.backBtn} onClick={() => navigate("/")}>
            <BackIcon />
            {!isMobile && (
              <span style={{ fontSize: 12, fontWeight: 600, fontFamily: FONT.body, letterSpacing: "0.4px" }}>
                {t("common.back")}
              </span>
            )}
          </button>

          {/* Center: Logo + brand */}
          <div style={s.navCenter}>
            <img src="/geo.png" alt="Logo" style={s.navLogo} />
            {!isMobile && (
              <div>
                <div style={s.navBrandTop}>GEORGIAN TAEKWONDO</div>
                <div style={s.navBrandBot}>NATIONAL FEDERATION</div>
              </div>
            )}
          </div>

          {/* Right: Rankings link + language switcher */}
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 10 }}>
            <button
              className="pp-nav-link"
              style={s.navTextLink}
              onClick={() => navigate("/rankings")}
            >
              <RankingsIcon />
              {!isMobile && <span>{t("landing.rankings")}</span>}
            </button>
            <LanguageSwitcher />
          </div>
        </header>}

        {/* ══════════════ HERO ════════════════════════════════════════════════ */}
        <div className={heroLoaded ? "pp-loaded" : ""} style={s.hero}>
          {/* Background layers */}
          <div style={s.heroBgWrap}>
            <div style={{
              ...s.heroBg,
              backgroundImage: darkTheme ? s.heroBg.backgroundImage : "none",
              background: darkTheme ? undefined : "radial-gradient(circle at 82% 22%, var(--color-accent-soft), transparent 30%), linear-gradient(145deg, var(--color-surface), var(--color-surface-2))",
              animation: heroLoaded ? "heroBgZoom 1.8s cubic-bezier(.22,1,.36,1) forwards" : "none",
            }} />
            {darkTheme && <><div style={s.heroOverlay1} /><div style={s.heroOverlay2} /><div style={s.heroOverlay3} /></>}
          </div>

          {/* Hero content */}
          <div style={{
            ...s.heroContent,
            padding: isMobile ? "52px 20px 60px" : "68px 80px 76px",
            alignItems: isMobile ? "center" : "flex-start",
            textAlign: isMobile ? "center" : "left",
          }}>
            <div className="pp-anim-0" style={s.heroEyebrow}>
              GEORGIAN TAEKWONDO FEDERATION
            </div>

            <div className="pp-anim-1" style={s.heroLockWrap}>
              <LockIcon />
            </div>

            <h1 className="pp-anim-2" style={{
              ...s.heroTitle,
              color: darkTheme ? C.heroText : C.text,
              fontSize: isMobile ? "clamp(40px,12vw,60px)" : "clamp(58px,7vw,88px)",
            }}>
              {t("privacyPolicy.title")}
            </h1>

            <p className="pp-anim-3" style={{
              ...s.heroSub,
              color: darkTheme ? C.heroText2 : C.text2,
              maxWidth: isMobile ? "100%" : "500px",
            }}>
              {t("privacyPolicy.subtitle")}
            </p>

            <div className="pp-anim-3" style={s.heroBadge}>
              {t("privacyPolicy.lastUpdated")}
            </div>
          </div>
        </div>

        {/* ══════════════ CONTENT ═════════════════════════════════════════════ */}
        <div style={{
          ...s.contentWrap,
          padding: isMobile ? "32px 14px 64px" : "48px 24px 80px",
        }}>
          <div style={s.contentInner}>

            {/* Intro card */}
            <div style={s.introCard}>
              <div style={s.introAccentBar} />
              <RichText text={t("privacyPolicy.intro")} style={s.introText as React.CSSProperties} />
            </div>

            {/* Section cards */}
            <div style={s.sectionsGrid}>
              {sections.map((sec, idx) => (
                <div
                  key={idx}
                  className="pp-section-card"
                  style={{
                    ...s.sectionCard,
                    transition: "border-color 0.2s, transform 0.2s, box-shadow 0.2s",
                  }}
                >
                  {/* Red left accent line */}
                  <div style={s.sectionAccentLine} />

                  <div style={s.sectionInner}>
                    <div style={s.sectionHeader}>
                      <div style={s.sectionNumber}>
                        {String(idx + 1).padStart(2, "0")}
                      </div>
                      <h2 style={s.sectionTitle}>{t(sec.titleKey)}</h2>
                    </div>
                    <RichText text={t(sec.bodyKey)} style={s.sectionBody as React.CSSProperties} />
                  </div>
                </div>
              ))}
            </div>

            {/* Contact box */}
            <div style={s.contactCard}>
              <div style={s.contactTopBar} />
              <div style={s.contactBody}>
                <div style={s.contactIconWrap}>
                  <MailIcon />
                </div>
                <div style={s.contactTitle}>{t("privacyPolicy.contact.title")}</div>
                <RichText text={t("privacyPolicy.contact.body")} style={s.contactText as React.CSSProperties} />
                <a
                  href="mailto:taekwondogeo@gmail.com"
                  className="pp-email-btn"
                  style={{
                    ...s.emailBtn,
                    transition: "opacity 0.15s, transform 0.15s",
                  }}
                >
                  <MailIcon />
                  taekwondogeo@gmail.com
                </a>
              </div>
            </div>

            {/* Footer note */}
            <div style={s.footerNote}>
              <p style={s.footerNoteText}>{t("privacyPolicy.footerNote")}</p>
              <button
                className="pp-home-btn"
                style={{
                  ...s.homeBtn,
                  transition: "background 0.15s, border-color 0.15s, color 0.15s",
                }}
                onClick={() => navigate("/")}
              >
                <HomeIcon />
                {t("privacyPolicy.backHome")}
              </button>
            </div>

          </div>
        </div>

        {/* ══════════════ FOOTER ══════════════════════════════════════════════ */}
        <footer style={{
          ...s.footer,
          padding: isMobile ? "16px 14px" : "18px 32px",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 10 : 0,
          textAlign: isMobile ? "center" : "left",
        }}>
          <div style={s.footerLeft}>
            <img src="/geo.png" alt="Logo" style={s.footerLogo} />
            <span style={s.footerText}>
              © {new Date().getFullYear()} Georgian Taekwondo Federation
            </span>
          </div>
          <div style={s.footerRight}>
            <button style={s.footerLink} onClick={() => navigate("/events")}>
              {t("landing.events")}
            </button>
            <span style={s.footerDivider}>·</span>
            <button style={s.footerLink} onClick={() => navigate("/rankings")}>
              {t("landing.rankings")}
            </button>
            <span style={s.footerDivider}>·</span>
            <button style={{ ...s.footerLink, color: "rgba(218,41,28,0.6)" }} onClick={() => navigate("/privacy")}>
              {t("privacyPolicy.title")}
            </button>
          </div>
        </footer>

      </div>
    </>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────
const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    backgroundColor: C.bg,
    fontFamily: FONT.body,
    display: "flex",
    flexDirection: "column",
  },

  // ── Navbar
  navbar: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    backgroundColor: "rgba(7,10,15,0.92)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    borderBottom: `1px solid ${C.border}`,
    height: 58,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  backBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "none",
    border: `1px solid ${C.border2}`,
    borderRadius: 8,
    color: C.text2,
    cursor: "pointer",
    padding: "6px 10px",
    fontFamily: FONT.body,
    flexShrink: 0,
  },
  navCenter: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
  },
  navLogo: {
    height: 28,
    objectFit: "contain",
  },
  navBrandTop: {
    fontSize: 11,
    fontWeight: 800,
    color: C.text,
    letterSpacing: "1.5px",
    lineHeight: 1.2,
    fontFamily: FONT.body,
  },
  navBrandBot: {
    fontSize: 9,
    fontWeight: 600,
    color: C.text2,
    letterSpacing: "1px",
    lineHeight: 1.2,
    fontFamily: FONT.body,
  },
  navTextLink: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "none",
    border: "none",
    color: C.text2,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: FONT.body,
    letterSpacing: "0.3px",
    padding: "4px 8px",
  },

  // ── Hero
  hero: {
    position: "relative",
    overflow: "hidden",
    minHeight: 340,
  },
  heroBgWrap: {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
  },
  heroBg: {
    position: "absolute",
    inset: 0,
    backgroundImage: "url('/hero-bg.jpg')",
    backgroundSize: "cover",
    backgroundPosition: "center 30%",
    willChange: "transform",
  },
  heroOverlay1: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(135deg, rgba(7,10,15,0.94) 0%, rgba(7,10,15,0.65) 100%)",
  },
  heroOverlay2: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(0deg, var(--color-bg) 0%, transparent 60%)",
  },
  heroOverlay3: {
    position: "absolute",
    inset: 0,
    background: "radial-gradient(ellipse at 15% 50%, rgba(218,41,28,0.1) 0%, transparent 60%)",
  },
  heroContent: {
    position: "relative",
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  heroEyebrow: {
    fontSize: 10,
    fontWeight: 800,
    color: C.red,
    letterSpacing: "3px",
    fontFamily: FONT.body,
  },
  heroLockWrap: {
    color: "rgba(218,41,28,0.8)",
    lineHeight: 0,
    marginBottom: -2,
  },
  heroTitle: {
    fontFamily: FONT.display,
    color: C.heroText,
    lineHeight: 0.95,
    letterSpacing: "1px",
    margin: 0,
  },
  heroSub: {
    fontSize: 14,
    color: C.heroText2,
    lineHeight: 1.65,
    margin: 0,
    fontFamily: FONT.body,
  },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 14px",
    backgroundColor: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 700,
    color: C.text2,
    letterSpacing: "1px",
    fontFamily: FONT.body,
    alignSelf: "flex-start",
  },

  // ── Content wrapper
  contentWrap: {
    flex: 1,
    backgroundColor: C.bg,
  },
  contentInner: {
    maxWidth: 780,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },

  // ── Intro card
  introCard: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  introAccentBar: {
    height: 3,
    background: "linear-gradient(90deg, #da291c, rgba(218,41,28,0.3))",
    flexShrink: 0,
  },
  introText: {
    margin: 0,
    padding: "18px 22px 20px",
    fontSize: 14,
    color: C.text2,
    lineHeight: 1.75,
    fontFamily: FONT.body,
  },

  // ── Sections grid
  sectionsGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  // ── Section card
  sectionCard: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    overflow: "hidden",
    display: "flex",
    flexDirection: "row",
    boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
  },
  sectionAccentLine: {
    width: 3,
    flexShrink: 0,
    background: "linear-gradient(180deg, #da291c 0%, rgba(218,41,28,0.25) 100%)",
    borderRadius: "14px 0 0 14px",
  },
  sectionInner: {
    flex: 1,
    padding: "18px 20px 20px",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 10,
  },
  sectionNumber: {
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 800,
    color: C.red,
    letterSpacing: "0.5px",
    fontFamily: FONT.body,
    lineHeight: 1,
    marginTop: 3,
    opacity: 0.8,
    minWidth: 22,
  },
  sectionTitle: {
    fontFamily: FONT.body,
    fontSize: 14,
    fontWeight: 700,
    color: C.text,
    margin: 0,
    lineHeight: 1.35,
    letterSpacing: "0.2px",
  },
  sectionBody: {
    margin: 0,
    fontSize: 13,
    color: C.text2,
    lineHeight: 1.8,
    fontFamily: FONT.body,
  },

  // ── Contact card
  contactCard: {
    backgroundColor: C.surface,
    border: "1px solid rgba(218,41,28,0.2)",
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 4px 24px rgba(218,41,28,0.06)",
  },
  contactTopBar: {
    height: 3,
    background: C.red,
  },
  contactBody: {
    padding: "28px 28px 30px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
    textAlign: "center",
  },
  contactIconWrap: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    backgroundColor: "rgba(218,41,28,0.1)",
    border: "1px solid rgba(218,41,28,0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: C.red,
    marginBottom: 2,
  },
  contactTitle: {
    fontFamily: FONT.body,
    fontSize: 15,
    fontWeight: 700,
    color: C.text,
    letterSpacing: "0.2px",
  },
  contactText: {
    margin: 0,
    fontSize: 13,
    color: C.text2,
    lineHeight: 1.7,
    maxWidth: 400,
    fontFamily: FONT.body,
  },
  emailBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 22px",
    backgroundColor: C.red,
    color: C.onAccent,
    borderRadius: 9,
    textDecoration: "none",
    fontSize: 13,
    fontWeight: 700,
    fontFamily: FONT.body,
    letterSpacing: "0.3px",
    marginTop: 4,
  },

  // ── Footer note
  footerNote: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 14,
    paddingTop: 8,
    paddingBottom: 8,
  },
  footerNoteText: {
    margin: 0,
    fontSize: 12,
    color: C.text3,
    textAlign: "center",
    lineHeight: 1.65,
    fontFamily: FONT.body,
  },
  homeBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "10px 20px",
    backgroundColor: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: 9,
    fontSize: 12,
    fontWeight: 600,
    color: C.text2,
    cursor: "pointer",
    fontFamily: FONT.body,
    letterSpacing: "0.3px",
  },

  // ── Footer
  footer: {
    backgroundColor: C.bg,
    borderTop: `1px solid ${C.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  footerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  footerLogo: {
    height: 20,
    objectFit: "contain",
    opacity: 0.4,
  },
  footerText: {
    fontSize: 11,
    color: C.text3,
    fontFamily: FONT.body,
  },
  footerRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  footerLink: {
    background: "none",
    border: "none",
    color: C.text3,
    fontSize: 11,
    cursor: "pointer",
    fontFamily: FONT.body,
    padding: 0,
  },
  footerDivider: {
    color: "var(--color-border-strong)",
    fontSize: 11,
  },
};
